import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { getIconClassName } from "../../../Findings/FindingsUtils";
import "../ConditionRowsPanel/ConditionRowsPanel.scss";
import { TextButton } from "@SeemplicityDev/shared";
import FilterChip from "../FilterChip/FilterChip";
import type {
  FieldData,
  FilterItem,
  FilterPinItem,
} from "../../FilterManagement/CommonFilterTypes";
import type { FilterEntity } from "../../FilterManagement/filterEntity";

export interface ConditionsRowProps {
  entity?: FilterEntity;
  namePrefix: string;
  previewFilter: (preview?: boolean) => void;
  rowIndex: number;
  showAddCondition?: boolean;
  onAddConditions?: () => void;
  disableAddCondition?: boolean;
  removeRow?: (index: number) => void;
  totalConditionsAmount?: number;
  showToggleConditionsButton?: boolean;
  toggleShowConditions?: () => void;
  showConditions?: boolean;
  onPopoverOpenChange: (isOpen: boolean) => void;
  fieldsData: FieldData[];
  isEditable?: boolean;
  onLastConditionRemove: () => void;
  context: string;
  showAddValue?: boolean;
  statusesData?: any[];
  shouldBlinkField?: string;
  shouldOpenField?: string;
  updateFilter: (newState: {
    shouldOpenField?: string | null;
    shouldBlinkField?: string | null;
  }) => void;
  allowRemoveAll?: boolean;
  isOnlyRow?: boolean;
  chipPopoverClassName?: string;
  filtersToPin?: FilterPinItem[];
  unsupportedFilterFields?: string[];
  addUsedField?: (field: string) => void;
  removeUsedField?: (field: string) => void;
}

const ConditionsRow: React.FC<ConditionsRowProps> = ({
  entity,
  namePrefix,
  previewFilter,
  rowIndex,
  showAddCondition,
  onAddConditions,
  disableAddCondition,
  removeRow,
  totalConditionsAmount,
  showToggleConditionsButton,
  toggleShowConditions,
  showConditions,
  onPopoverOpenChange,
  fieldsData,
  isEditable,
  onLastConditionRemove,
  context,
  showAddValue,
  statusesData,
  shouldBlinkField,
  shouldOpenField,
  updateFilter,
  allowRemoveAll,
  isOnlyRow,
  chipPopoverClassName,
  filtersToPin = [],
  unsupportedFilterFields = [],
  addUsedField,
  removeUsedField,
}) => {
  const { control, formState } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${namePrefix}.conditions`, // unique name for your Field Array,
    rules: {
      required: true,
      validate: (value) => {
        if (Array.isArray(value)) {
          return (value as FilterItem[])?.every(
            (conditionRow) =>
              conditionRow.condition !== null &&
              conditionRow.value !== null &&
              conditionRow.field !== null
          );
        }
        return true;
      },
    },
  });

  const [openChipId, setOpenChipId] = useState<string | null>(null);
  const isChipPopoverOpen = openChipId !== null;

  const watchFieldArray =
    useWatch({
      control,
      name: `${namePrefix}.conditions`,
    }) || [];

  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  useEffect(() => {
    onPopoverOpenChange(isChipPopoverOpen);

    return () => {
      if (isChipPopoverOpen) {
        onPopoverOpenChange(false);
      }
    };
  }, [isChipPopoverOpen]);

  useEffect(() => {
    if (!controlledFields?.length) {
      append({ field: null, condition: null, value: "" });
    }
  }, [controlledFields?.length]);

  const allowRemove = useMemo(
    () => allowRemoveAll || controlledFields?.length > 1 || !isOnlyRow,
    [controlledFields]
  );

  const getIsUnsupported = useCallback(
    (field: string | null | undefined) => {
      return !!field && unsupportedFilterFields?.includes(field);
    },
    [unsupportedFilterFields]
  );

  const resetOpenField = useCallback(
    () => updateFilter({ shouldOpenField: null }),
    [updateFilter]
  );

  return (
    <div className={`chips-rows ${showConditions ? "toggle-is-open" : ""}`}>
      {!!fieldsData?.length &&
        controlledFields?.map((filterObject, index) => (
          <Fragment key={filterObject?.id}>
            <Controller
              name={`${namePrefix}.conditions[${index}]`}
              render={({ field: { onChange, value } }) => {
                return (
                  <Fragment key={filterObject?.id}>
                    <FilterChip
                      entity={entity}
                      onFilterSelect={(filter) => {
                        onChange(filter);
                        previewFilter(true);
                      }}
                      addUsedField={addUsedField}
                      removeUsedField={removeUsedField}
                      filterItem={value}
                      namePrefix={`${namePrefix}.conditions[${index}]`}
                      previewFilter={previewFilter}
                      itemIndex={index}
                      fieldsData={fieldsData}
                      rowIndex={rowIndex}
                      removeFilter={() => {
                        const isLastCondition =
                          watchFieldArray?.length === 1 && index === 0;
                        if (isLastCondition) {
                          onLastConditionRemove();
                        }
                        remove(index);
                      }}
                      removeRow={removeRow}
                      setMenuOpen={(isOpen) =>
                        setOpenChipId((prevChipId) =>
                          isOpen
                            ? filterObject?.id
                            : prevChipId === filterObject?.id
                              ? null
                              : prevChipId
                        )
                      }
                      isEditable={isEditable}
                      context={context}
                      statusesData={statusesData}
                      shouldBlinkField={shouldBlinkField}
                      resetBlinkField={() =>
                        setTimeout(() => {
                          updateFilter({ shouldBlinkField: null });
                        }, 2000)
                      }
                      shouldOpenField={shouldOpenField}
                      resetOpenField={resetOpenField}
                      allowRemove={allowRemove}
                      addValue={({ field, condition, value }) =>
                        append({ field, condition, value })
                      }
                      chipPopoverClassName={chipPopoverClassName}
                      filtersToPin={filtersToPin}
                      isUnsupported={getIsUnsupported(value?.field)}
                    />
                  </Fragment>
                );
              }}
              defaultValue={filterObject}
            />
          </Fragment>
        ))}
      {showAddValue && (
        <TextButton
          wrapperClassName={"panel-text-btn"}
          data-testid={`add-condition-value-${context?.toLowerCase()}`}
          label={"Add Filter"}
          leftIcon={getIconClassName(context?.toLowerCase())}
          buttonStyle={"textLink"}
          disabled={!formState?.isValid}
          data-analytics-event="user_click"
          data-analytics-props={`{"name": "add_filter_value_btn", "container": "${context?.toLowerCase()}"}`}
          onClick={() => {
            append({ field: null, condition: null, value: null });
          }}
        />
      )}
      {showAddCondition && (
        <TextButton
          data-testid={`scope-add-condition`}
          wrapperClassName={"panel-text-btn"}
          className={`scope-add-condition`}
          label={"Add Condition"}
          type={"button"}
          buttonStyle={"textLink"}
          leftIcon={"seem-icon-condition"}
          disabled={disableAddCondition}
          onClick={onAddConditions}
        />
      )}
      {showToggleConditionsButton && (
        <TextButton
          wrapperClassName={`panel-text-btn panel-text-btn-conditions-btn ${
            showConditions ? "panel-text-btn-conditions-btn-open" : ""
          }`}
          data-testid={"toggle-show-conditions"}
          type={"button"}
          label={`OR (${totalConditionsAmount})`}
          leftIcon={"seem-icon-condition"}
          rightIcon={
            showConditions ? "seem-icon-chevron-up" : "seem-icon-chevron-down"
          }
          buttonStyle={"textLink"}
          onClick={toggleShowConditions}
        />
      )}
    </div>
  );
};

export default ConditionsRow;
